import type { QuestionnaireItemDto } from "@/models/dto/questionnaireItemSetDto";
import { QuestionType } from "@/models/enum/questionType";
import { AnswerKeyPersonnel } from "@/models/persons/keyPersonnel";
import { AnswerBeneficialOwner } from "@/models/persons/beneficialOwner";
import type { AnswerIndividualOwner } from "@/models/persons/individualOwner";
import type { AnswerCompanyOwner } from "@/models/questions/companyOwner";
import type {
  AnswerPersonWithPepAndSanctions,
  PersonWithPepAndSanctions,
} from "@/models/persons/personWithPepAndSanctions";
import type { AnswerPersonWithNameAndPosition } from "@/models/persons/personWithNameAndPosition";

const stringQuestionTypes = [
  QuestionType.Text,
  QuestionType.Select,
  QuestionType.Date,
  QuestionType.TextArea,
];

export const answerSetIsComplete = (answerSet: QuestionnaireItemDto[]) => {
  const someRequiredAnswersAreNull = answerSet.some(
    (x) => !x.answers && (x.questionModel.validations?.required ?? true)
  );

  if (someRequiredAnswersAreNull) {
    return false;
  }

  for (const answer of answerSet) {
    if (!answer.questionModel.validations?.required ?? true) {
      continue;
    }

    if (stringQuestionTypes.includes(answer.questionModel.question.type)) {
      if (
        typeof answer.answers === "string" &&
        answer.answers.trim().length === 0
      ) {
        return false;
      }
      continue;
    }

    if (answer.questionModel.question.type === QuestionType.Multiselect) {
      if (Array.isArray(answer.answers) && answer.answers.length === 0) {
        return false;
      }
      continue;
    }

    if (answer.questionModel.question.type === QuestionType.ListOfCheckboxes) {
      if (
        Array.isArray(answer.answers) &&
        answer.answers.every((item) => item["checked"] === false)
      ) {
        return false;
      }
    }
  }
  return true;
};

export function personnelAnswerComplete(
  answers: AnswerKeyPersonnel[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else if (Object.values(answers[0]).every((x) => x === null || x === "")) {
    return false;
  } else {
    return true;
  }
}

export function individualOwnershipAnswerComplete(
  answers: AnswerIndividualOwner[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else return !individualOwnerIsEmpty(answers[0]);
}

export function personsWithPepAndSanctionsAnswerComplete(
  answers: AnswerPersonWithPepAndSanctions[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else return !personWithPepAndSanctionsIsEmpty(answers[0]);
}

function personWithPepAndSanctionsIsEmpty(
  o: PersonWithPepAndSanctions
): boolean {
  const { ["pepRca"]: pepRca, ...everyThingElse } = o;
  return objectIsEmpty(pepRca) && objectIsEmpty(everyThingElse);
}

export function personsWithNameAndPositionAnswerComplete(
  answers: AnswerPersonWithNameAndPosition[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else return !objectIsEmpty(answers[0]);
}

function individualOwnerIsEmpty(o: AnswerIndividualOwner): boolean {
  const { ["address"]: address, ["pepRca"]: pepRca, ...everyThingElse } = o;
  return (
    objectIsEmpty(address) &&
    objectIsEmpty(pepRca) &&
    objectIsEmpty(everyThingElse)
  );
}

export function companyOwnershipAnswerComplete(
  answers: AnswerCompanyOwner[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else return !companyOwnerIsEmpty(answers[0]);
}

function companyOwnerIsEmpty(o: AnswerCompanyOwner): boolean {
  const { ["address"]: address, ...everyThingElse } = o;
  return objectIsEmpty(address) && objectIsEmpty(everyThingElse);
}

export function ownershipAnswerComplete(
  answers: AnswerBeneficialOwner[] | null | undefined
) {
  if (answers === null || answers === undefined) {
    return false;
  } else if (answers.length > 1) {
    return true;
  } else return !beneficialOwnerIsEmpty(answers[0]);
}

function beneficialOwnerIsEmpty(o: AnswerBeneficialOwner): boolean {
  const { ["address"]: address, ...answerNoAddress } = o;
  return objectIsEmpty(address) && objectIsEmpty(answerNoAddress);
}

export function objectIsEmpty(obj: object): boolean {
  return Object.values(obj).every((v) => v === null || v === "");
}
